/* GrvMobileUpload — Lightweight phone capture page reached by scanning
 * the QR code shown in the desktop AiInvoiceImportModal.
 *
 * Token in the URL is the only credential. Each captured page is POSTed
 * straight to the backend handoff session; the desktop polls and slots
 * the image into its multi-page tray.
 */
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Camera, CheckCircle2, Loader2, RefreshCcw, Upload, XCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function GrvMobileUpload() {
  const { token } = useParams();
  const [status, setStatus] = useState("checking");   // checking | ready | invalid | closed
  const [uploadCount, setUploadCount] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [recent, setRecent] = useState([]);   // [{previewUrl, ok}]
  const fileInputRef = useRef(null);

  // Verify token on mount
  useEffect(() => {
    const verify = async () => {
      try {
        const r = await axios.get(`${API}/api/grv/handoff/${token}/status`);
        if (r.data.status === "closed") {
          setStatus("closed");
        } else {
          setStatus("ready");
          setUploadCount(r.data.image_count || 0);
        }
      } catch (e) {
        setStatus("invalid");
      }
    };
    verify();
  }, [token]);

  // Poll status every 5 s so we can show "Closed by desktop" promptly.
  useEffect(() => {
    if (status !== "ready") return;
    const id = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/api/grv/handoff/${token}/status`);
        if (r.data.status === "closed") setStatus("closed");
        else setUploadCount(r.data.image_count || 0);
      } catch (_e) { /* ignore transient */ }
    }, 5000);
    return () => clearInterval(id);
  }, [status, token]);

  const handleFiles = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setError("");
    for (const file of Array.from(fileList)) {
      setBusy(true);
      const previewUrl = URL.createObjectURL(file);
      try {
        const form = new FormData();
        form.append("file", file);
        await axios.post(`${API}/api/grv/handoff/${token}/upload`, form, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 60000,
        });
        setRecent((cur) => [{ previewUrl, ok: true }, ...cur].slice(0, 12));
        setUploadCount((n) => n + 1);
      } catch (e) {
        setRecent((cur) => [{ previewUrl, ok: false }, ...cur].slice(0, 12));
        setError(e?.response?.data?.detail || "Upload failed — please retry");
      } finally {
        setBusy(false);
      }
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  if (status === "checking") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center text-slate-600">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
          Checking session…
        </div>
      </div>
    );
  }

  if (status === "invalid" || status === "closed") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="bg-white border rounded-lg p-6 max-w-sm text-center shadow">
          <XCircle className="h-10 w-10 mx-auto text-red-500 mb-2" />
          <div className="font-semibold text-lg mb-1">
            {status === "closed" ? "Session closed" : "Session not found"}
          </div>
          <p className="text-sm text-slate-600">
            {status === "closed"
              ? "The desktop side has finished with this session. Please scan a new QR code from the AI Invoice Import dialog."
              : "This handoff link is no longer valid or has expired. Open the AI Invoice Import dialog on the desktop and scan a fresh QR code."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      <header className="bg-purple-700 text-white p-4 sticky top-0 z-10 shadow">
        <h1 className="text-lg font-semibold flex items-center gap-2">
          <Camera className="h-5 w-5" />
          Phone Invoice Capture
        </h1>
        <p className="text-xs opacity-80 mt-0.5">
          Linked to desktop · {uploadCount} page{uploadCount === 1 ? "" : "s"} sent
        </p>
      </header>

      <main className="p-4 space-y-4 max-w-md mx-auto">
        <div className="bg-white border rounded-lg p-4 shadow-sm">
          <p className="text-sm text-slate-700 mb-3">
            Snap each invoice page. They appear on the desktop screen instantly.
            When you're done, finish the import on the desktop.
          </p>

          <label
            className="block w-full bg-purple-600 hover:bg-purple-700 active:bg-purple-800 text-white font-semibold rounded-lg p-5 text-center cursor-pointer transition"
            data-testid="capture-page-btn"
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,application/pdf"
              capture="environment"
              className="hidden"
              onChange={(e) => handleFiles(e.target.files)}
            />
            {busy ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="h-5 w-5 animate-spin" /> Uploading…
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <Camera className="h-5 w-5" /> Snap invoice page
              </span>
            )}
          </label>

          <label
            className="block mt-2 w-full bg-white border-2 border-purple-300 text-purple-700 font-semibold rounded-lg p-3 text-center cursor-pointer hover:bg-purple-50"
            data-testid="pick-from-gallery-btn"
          >
            <input
              type="file"
              accept="image/*,application/pdf"
              multiple
              className="hidden"
              onChange={(e) => handleFiles(e.target.files)}
            />
            <span className="flex items-center justify-center gap-2">
              <Upload className="h-4 w-4" /> Pick from gallery / file
            </span>
          </label>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded p-3 flex items-start gap-2">
            <XCircle className="h-4 w-4 mt-0.5 flex-shrink-0" /> {error}
          </div>
        )}

        {recent.length > 0 && (
          <div className="bg-white border rounded-lg p-3 shadow-sm">
            <div className="text-xs font-semibold text-slate-600 mb-2">
              Recently uploaded
            </div>
            <div className="grid grid-cols-3 gap-2">
              {recent.map((r, idx) => (
                <div key={idx} className="relative aspect-square rounded overflow-hidden border bg-slate-100">
                  <img src={r.previewUrl} alt="" className="w-full h-full object-cover" />
                  <div className={`absolute top-1 right-1 rounded-full p-0.5 ${r.ok ? "bg-green-600" : "bg-red-600"}`}>
                    {r.ok ? <CheckCircle2 className="h-3 w-3 text-white" /> : <RefreshCcw className="h-3 w-3 text-white" />}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <p className="text-[11px] text-slate-500 text-center pt-2">
          Keep this page open until you've sent all pages. Finish the import on
          the desktop screen.
        </p>
      </main>
    </div>
  );
}
